import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, exhaustMap, map, switchMap, tap } from "rxjs/operators";
import { LotService } from "src/app/honey-house/lot.service";
import { FeedbackService } from "src/app/shared/feedback.service";
import { fetchLots, fetchLotsSuccess, fetchLotsError, fetchLot, fetchLotSuccess, fetchLotError, fetchAmounts, amountsFetched, amountsFetchedError, lotSubmitted, lotSubmittedSuccess, lotSubmittedError, deleteLot, deleteLotSuccess, deleteLotError, assignHarvest, updateHarvestOk, updateHarvestErr, removeHarvest, connectLabResult, connectLabResultSuccess, connectLabResultError, unsetLabConnect, labConnectOk, labConnectErr, fetchBeekeeperProducts, fetchBeekeeperProductsSuccess, fetchBeekeeperProductsError, beekeeperProductsSubmitted, beekeeperProductsSubmittedSuccess, beekeeperProductsSubmittedError } from "./lot.actions";
import { HarvestAmountService } from "src/app/honey-house/harvest-amount.service";
import { LotConnectService } from "src/app/honey-house/lot-connect.service";
import { BeekeeperProductsService } from "src/app/honey-house/beekeeper-products.service";

@Injectable()
export class LotEffects {
  fetchLots$ = createEffect(() =>
    this.actions.pipe(
      ofType(fetchLots),
      exhaustMap(() =>
        this.lotService.getItems().pipe(
          map(response => fetchLotsSuccess({ lots: response })),
          catchError(error => of(fetchLotsError({ error })))
        )
      )
    )
  );

  fetchLot$ = createEffect(() =>
    this.actions.pipe(
      ofType(fetchLot),
      switchMap(({ lotId }) =>
        this.lotService.getOne(lotId).pipe(
          map(response => fetchLotSuccess({ lot: response })),
          catchError(error => of(fetchLotError({ error })))
        )
      )
    )
  );

  // Fetch *all* amounts
  fetchLotAmounts$ = createEffect(() =>
    this.actions.pipe(
      ofType(fetchLot, fetchAmounts),
      exhaustMap(() => this.amounts.getAmounts().pipe(
        map(response => amountsFetched({ response })),
        catchError(error => of(amountsFetchedError({ error })))
      )
      )
    )
  );

  saveLot$ = createEffect(() =>
    this.actions.pipe(
      ofType(lotSubmitted),
      exhaustMap((action) =>
        this.lotService.saveItem(action.lot).pipe(
          map(response => lotSubmittedSuccess({
            lot: response,
            redirectTo: action.redirectTo
          })),
          catchError(error => of(lotSubmittedError({ error })))
        )
      )
    )
  );

  saveLotSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(lotSubmittedSuccess),
      tap((resp) => {
        this.feedbackService.success('Saved!');
        if (resp.redirectTo) {
          this.router.navigate([resp.redirectTo]);
        } else {
          // After creating a new lot, navigate to its edit page
          // so the user can add amounts and attachments.
          this.router.navigate(['/harvest/lots', resp.lot.id]);
        }
      }),
      map(() => fetchLots())
    )
  );

  saveLotError$ = createEffect(() =>
    this.actions.pipe(
      ofType(lotSubmittedError),
      tap((resp) => {
        this.feedbackService.error('Could not be saved', {});
        // this.router.navigate(['..'])
      })
    ), { dispatch: false });

  deleteLot$ = createEffect(() =>
    this.actions.pipe(
      ofType(deleteLot),
      exhaustMap(({ lotId, redirectTo }) =>
        this.lotService.deleteItem(lotId).pipe(
          map((response) => deleteLotSuccess({ redirectTo })),
          catchError(error => of(deleteLotError))
        ))
    )
  );

  deleteLotSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(deleteLotSuccess),
      tap((resp) => {
        this.feedbackService.success('Deleted!');
        if (resp.redirectTo) {
          this.router.navigate([resp.redirectTo])
        }
      }),
      map(() => fetchLots())
    )
  );

  deleteLotError$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(deleteLotError),
        tap(() => {
          this.feedbackService.error('Something went wrong!');
        })
      ),
    { dispatch: false }
  );

  assignHarvest$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(assignHarvest),
        exhaustMap(({ harvestAmount}) =>
          this.amounts.assignHarvest(harvestAmount).pipe(
            map((response) => updateHarvestOk({ harvestAmount })),
            catchError(error => of(updateHarvestErr({error})))
          ))
      ),
  );

  manageHarvestOk$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(updateHarvestOk),
        tap(() => {
          this.feedbackService.error('Harvest updated');
        }),
        // Re-load amounts
        // NOTE: this is heavy on the network / backend
        //    alternative would be to update the state
        //    advantage is to always have backend data synced
        map(() => fetchAmounts())
      )
  );

  manageHarvestFail$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(updateHarvestErr),
        tap(() => {
          this.feedbackService.error('SHARED.MESSAGES.UPDATE_ERROR');
        })
      ),
    { dispatch: false }
  );

  removeHarvest$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(removeHarvest),
        exhaustMap(({harvestAmount}) =>
          this.amounts.removeAssignment(harvestAmount).pipe(
            map((response) => updateHarvestOk({ harvestAmount })),
            catchError(error => of(updateHarvestErr({error})))
          ))
      ),
  );

  connectLabResult$ = createEffect(() =>
    this.actions.pipe(
      ofType(connectLabResult),
      exhaustMap(({lotId, labresultSecretCode}) =>
        this.lotConnect.updateItem(lotId, {id: lotId, labresultSecretCode}).pipe(
          map((response) => connectLabResultSuccess({ lot: response })),
          catchError(error => of(connectLabResultError({error})))
        )
      )
    )
  );

  connectLabResultSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(connectLabResultSuccess),
      tap(() => {
        this.feedbackService.success('HARVEST.LOTS.LAB_CONNECT_SUCCESS');
      }),
      map(({lot}) => fetchLot({lotId: lot.id}))
    )
  );

  connectLabResultError$ = createEffect(() =>
    this.actions.pipe(
      ofType(connectLabResultError),
      tap(() => {
        this.feedbackService.error('HARVEST.LOTS.LAB_CONNECT_ERROR');
      })
    ),
    { dispatch: false }
  );

  unsetLabConnect$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(unsetLabConnect),
        exhaustMap(({lot}) =>
          this.lotConnect.deleteItem(lot.id).pipe(
            map((response) => labConnectOk({ lot })),
            catchError(error => of(labConnectErr({error})))
          ))
      ),
  );

  labConnectFail$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(labConnectErr),
        tap(() => {
          this.feedbackService.error('SHARED.MESSAGES.UPDATE_ERROR');
        })
      ),
    { dispatch: false }
  );

  labConnectOk$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(labConnectOk),
        tap(() => {
          this.feedbackService.success('SHARED.MESSAGES.SAVED');
        }),
        map(({lot}) => fetchLot({lotId: lot.id}))
      )
  );

  fetchBeekeeperProducts$ = createEffect(() =>
    this.actions.pipe(
      ofType(fetchBeekeeperProducts),
      exhaustMap(() =>
        this.beekeeperProductsService.getItems().pipe(
          map(response => fetchBeekeeperProductsSuccess({ beekeeperProducts: response })),
          catchError(error => of(fetchBeekeeperProductsError({ error })))
        )
      )
    )
  );

  saveBeekeeperProducts$ = createEffect(() =>
    this.actions.pipe(
      ofType(beekeeperProductsSubmitted),
      exhaustMap((action) =>
        this.beekeeperProductsService.saveItem(action.beekeeperProducts).pipe(
          map(response => beekeeperProductsSubmittedSuccess({ beekeeperProducts: response })),
          catchError(error => of(beekeeperProductsSubmittedError({ error })))
        )
      )
    )
  );

  saveBeekeeperProductsSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(beekeeperProductsSubmittedSuccess),
      tap(() => {
        this.feedbackService.success('SHARED.MESSAGES.SAVED');
      }),
      map(() => fetchBeekeeperProducts())
    )
  );

  saveBeekeeperProductsError$ = createEffect(() =>
    this.actions.pipe(
      ofType(beekeeperProductsSubmittedError),
      tap(() => {
        this.feedbackService.error('SHARED.MESSAGES.ERROR_SAVE');
      })
    ),
    { dispatch: false }
  );

  constructor(
    private actions: Actions,
    private lotService: LotService,
    private lotConnect: LotConnectService,
    private amounts: HarvestAmountService,
    private beekeeperProductsService: BeekeeperProductsService,
    private feedbackService: FeedbackService,
    private router: Router,
  ) { }
}
